import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookingFlow } from '@features/booking/flow';
import { useReservationCommit } from '@features/booking/hooks/useReservationCommit';

/**
 * Result shape returned to commit-host pages (Personalize, the in-flow
 * Guarantee payment forms — T14).
 *
 * The fields proxy `useReservationCommit` when this page is the commit host
 * (i.e. the next step in the active step set is BOOKING_FINAL) and return
 * neutral defaults otherwise — so the caller renders one button + one
 * error region against a single hook regardless of where it sits in the flow.
 *
 * `commitOrAdvance` is the single click handler:
 * - **willCommit=true** → calls `commit()` which posts the reservation,
 *   fires telemetry, maps errors to user-visible copy, and navigates to
 *   `/booking-final` on success via the goToNext() captured inside
 *   `commitReservation`.
 * - **willCommit=false** → calls `goToNext()` which advances the flow to
 *   whichever step is next (legacy path: PERSONALIZE → CONFIRMATION → …;
 *   when the flag drops CONFIRMATION, see "Dispatch logic" below).
 *
 * ## Dispatch logic — why "next step is BOOKING_FINAL" instead of the flag
 *
 * Earlier drafts of this hook dispatched directly on
 * `bookingState.skipConfirmStep`. That was incomplete: if a user reached
 * Guarantee with Personalize still ahead, `skipConfirmStep === true` would
 * incorrectly commit before personalization was collected.
 *
 * The correct seam is "is the current step the second-to-last?" — which
 * is exactly what `useBookingFlow.getNextStep()?.id === 'BOOKING_FINAL'`
 * answers. CONFIRMATION's `isRequired` predicate in `flow/steps.ts` drops
 * the step when the flag is true, so:
 * - flag ON  → CONFIRMATION not in step set → previous step's next is
 *   BOOKING_FINAL → commit
 * - flag OFF → CONFIRMATION in step set → previous step's next is
 *   CONFIRMATION → advance via goToNext()
 *
 * This means the flag is read in one place (steps.ts) and every consumer of
 * the flow naturally agrees on whether to commit.
 */
/**
 * Snapshot of next-step intent at the moment of the user's click. Callers
 * that mutate booking state between click and dispatch (payment forms
 * calling `flushSync` to publish a freshly minted card token) MUST capture
 * this BEFORE the mutation and pass it to `commitOrAdvance`. Without the
 * snapshot the post-flushSync re-render filters the current step out of
 * `activeSteps` (e.g. GUARANTEE's `isRequired: (ctx) => !ctx.hasCardToken`
 * removes it once cardToken is set), `currentStepIndex` drops to -1,
 * `getNextStep()` returns null, and the dispatch silently no-ops — the
 * user clicks the CTA and nothing happens (real bug 2026-05-21, e2e
 * regression in skip-confirm-commit-host.spec.ts).
 */
export interface CapturedCommitIntent {
  willCommit: boolean;
  /**
   * Path of the next step at click time. Needed for the advance branch so
   * we can navigate to the captured destination instead of re-deriving it
   * from the (now stale) active step list.
   */
  nextStepPath: string | null;
}

export interface UseCommitOrAdvanceResult {
  /**
   * Dispatch the commit or advance based on captured (or live) intent.
   * Hosts without mid-click state mutation (Personalize) can call with
   * no arguments — the hook reads live state. Payment forms must pass
   * the click-time snapshot. See `CapturedCommitIntent` for the why.
   */
  commitOrAdvance: (captured?: CapturedCommitIntent) => Promise<void>;
  /**
   * True iff the next click will commit the reservation rather than advance
   * the flow. Pages use this to switch the CTA label ("Reserve" vs
   * "Continue") and show payment-related affordances.
   */
  willCommit: boolean;
  /**
   * Path of the next step at the current render. Capture this synchronously
   * at click time (along with `willCommit`) and pass both to
   * `commitOrAdvance` when the host mutates booking state mid-click.
   */
  nextStepPath: string | null;
  /** True while a commit is in-flight (commit branch only). */
  isSubmitting: boolean;
  /** User-visible commit error message; `null` in advance branch. */
  error: string | null;
  /** MP-only: prompt the user to refresh after a 3DS-triggered v3 load. */
  requireReload: boolean;
  /** True while a 3DS bank-challenge overlay is mounted. */
  isHandling3ds: boolean;
}

/**
 * Step-aware "Continue or Reserve" hook for the Confirm-step removal rollout
 * (Phase 2, T13 + T14 in `docs/plans/remove-confirm-step.md`).
 *
 * Owns the branch decision for whichever page sits second-to-last in the
 * active step set so the page can render a single CTA against a single hook
 * regardless of the per-tenant flag.
 *
 * When `willCommit` is false the hook is a thin pass-through over
 * `goToNext()`; when true it delegates to the proven `useReservationCommit`
 * path — same telemetry, same error mapping, same 3DS overlay.
 *
 * Note: `useReservationCommit` subscribes to restaurant/auth/analytics
 * unconditionally. That's fine on every page in the flow — all of them are
 * mounted inside `TenantGuard` (restaurant guaranteed non-null), and the
 * providers are top-level so subscription is free.
 */
export function useCommitOrAdvance(): UseCommitOrAdvanceResult {
  const { getNextStep, goToNext } = useBookingFlow();
  const reservationCommit = useReservationCommit();
  const navigate = useNavigate();

  const nextStepAtRender = getNextStep();
  const liveWillCommit = nextStepAtRender?.id === 'BOOKING_FINAL';

  // Latch the commit-host identity for the lifetime of this mount. A payment
  // form publishes booking.cardToken via flushSync before its commit await
  // resolves; that filters GUARANTEE out of activeSteps (isRequired ===
  // !hasCardToken), so getNextStep() returns null and `liveWillCommit` flips to
  // false WHILE the user is still on the page. If the commit then fails (card
  // declined), the live value reads "not a commit host" and a retry click would
  // no-op/advance instead of re-posting. Once a page has been the commit host
  // we keep it one: success navigates to BOOKING_FINAL (unmount resets the ref),
  // failure keeps the user here to retry. Non-commit-host pages never latch.
  const wasCommitHostRef = useRef(false);
  const commitHostNextPathRef = useRef<string | null>(null);
  if (liveWillCommit) {
    wasCommitHostRef.current = true;
    commitHostNextPathRef.current = nextStepAtRender?.path ?? null;
  }
  const willCommit = wasCommitHostRef.current;
  const nextStepPath = willCommit
    ? commitHostNextPathRef.current
    : (nextStepAtRender?.path ?? null);

  // T15 synchronous in-flight guard. `useReservationCommit.commit` flips
  // `isSubmitting` inside an async chain (after guards pass), so a second
  // click that arrives within the same task tick — before that setState
  // commits — would slip past the `disabled={isSubmitting}` button gate and
  // double-fire the reservation POST. A ref flipped synchronously at the
  // top of the callback closes that window: the second invocation returns
  // immediately without calling commit/goToNext. Reset in a `finally` so a
  // failed commit (network error, 4xx) leaves the user free to retry.
  //
  // Why not move this into useReservationCommit? Because (a) the advance
  // branch (goToNext) also needs dedupe — a synchronous double-click on the
  // legacy /confirmation-bound path could fire two navigations and confuse
  // history, and (b) keeping the guard at the hook boundary means every
  // commit host (Personalize + Stripe + MercadoPago forms) gets it for
  // free without each page re-implementing the pattern.
  const inFlightRef = useRef(false);

  const commitOrAdvance = useCallback(async (captured?: CapturedCommitIntent) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      // Use the click-time snapshot when provided (payment-form host case
      // — see CapturedCommitIntent JSDoc for the flushSync-invalidation
      // hazard). Otherwise fall back to live values from this render.
      const effectiveWillCommit = captured?.willCommit ?? willCommit;
      const effectiveNextPath = captured?.nextStepPath ?? nextStepPath;
      if (effectiveWillCommit) {
        await reservationCommit.commit();
      } else if (effectiveNextPath) {
        // Navigate to the captured path directly instead of `goToNext()`,
        // which reads through `navStateRef.current` and would re-derive
        // from the (post-flushSync) active step list — same hazard the
        // CapturedCommitIntent exists to avoid.
        navigate(effectiveNextPath);
      } else {
        // No captured path AND nothing live — best-effort fallback.
        // Should not happen in practice for commit hosts.
        goToNext();
      }
    } finally {
      // Defer the release by a microtask so a synchronous second call
      // (e.g. two rapid native clicks in the same task tick) still sees
      // the lock as held. Without this, the advance branch (synchronous
      // goToNext) would release the lock before the second call evaluates,
      // and a double-tap would fire two navigations. queueMicrotask is the
      // right primitive: it runs after the current synchronous block
      // completes but before any I/O.
      queueMicrotask(() => {
        inFlightRef.current = false;
      });
    }
  }, [willCommit, nextStepPath, reservationCommit, navigate, goToNext]);

  // Surface the commit hook's state unconditionally. In pure-advance usage the
  // commit hook is never invoked, so these stay neutral — passing them through
  // is safe and avoids re-introducing the willCommit-gated drop that hid the
  // decline error (the latch above keeps a commit host's willCommit true, but
  // not gating here is the simpler, defensive contract).
  return {
    commitOrAdvance,
    willCommit,
    nextStepPath,
    isSubmitting: reservationCommit.isSubmitting,
    error: reservationCommit.error,
    requireReload: reservationCommit.requireReload,
    isHandling3ds: reservationCommit.isHandling3ds,
  };
}
